/**
 * Helpers for `form_json` top-level sections with optional `subsections`.
 * Legacy: only `section.fields` (no or empty `subsections`).
 * Subsections: `section.subsections` is a non-empty array of `{ heading, fields }`.
 */

/** Field types that collect user input during an audit (not display-only content). */
export const FILLABLE_AUDIT_FIELD_TYPES = [
    'text',
    'textarea',
    'date',
    'select',
    'multi-select',
    'checkbox',
    'checkbox-group',
    'range',
    'radio',
    'file-upload',
    'audit-import',
];

/**
 * @param {string | undefined} type
 * @returns {boolean}
 */
export function isFillableAuditFieldType(type) {
    return FILLABLE_AUDIT_FIELD_TYPES.includes(type);
}

/**
 * @param {Record<string, unknown>} section
 * @returns {'legacy' | 'subsections'}
 */
export function getSectionMode(section) {
    if (!section || typeof section !== 'object' || Array.isArray(section)) {
        return 'legacy';
    }
    const subs = section.subsections;
    if (Array.isArray(subs) && subs.length > 0) {
        return 'subsections';
    }
    return 'legacy';
}

/**
 * All form fields in display order for one top-level section (for scoring, gaps, O&E).
 * @param {Record<string, unknown>} section
 * @returns {Array<Record<string, unknown>>}
 */
export function getAllFieldsForSection(section) {
    if (getSectionMode(section) === 'subsections') {
        return (section.subsections || []).flatMap((sub) =>
            Array.isArray(sub?.fields) ? sub.fields : []
        );
    }
    return Array.isArray(section?.fields) ? section.fields : [];
}

/**
 * Walk every field in a section with stable indices (for gap row ids, PDF, etc.).
 * @param {Record<string, unknown>} section
 * @param {(args: { field: Record<string, unknown>, subIndex: number, fieldIndex: number }) => void} callback
 */
export function forEachFieldInSection(section, callback) {
    if (getSectionMode(section) === 'subsections') {
        (section.subsections || []).forEach((sub, subIndex) => {
            const fields = Array.isArray(sub?.fields) ? sub.fields : [];
            fields.forEach((field, fieldIndex) => {
                if (
                    field &&
                    typeof field === 'object' &&
                    !Array.isArray(field)
                ) {
                    callback({ field, subIndex, fieldIndex });
                }
            });
        });
        return;
    }
    const fields = Array.isArray(section?.fields) ? section.fields : [];
    fields.forEach((field, fieldIndex) => {
        if (field && typeof field === 'object' && !Array.isArray(field)) {
            callback({ field, subIndex: 0, fieldIndex });
        }
    });
}
